<template>
  <PageHeader
    v-show="!isCheckout"
    :logo-link="logoLink"
    :logo-text="$globalPageSettings?.companyName ?? ''"
    :main-navigation="mainNavigationItems"
    :nav-top-items="navTopItems"
    :nav-action-bar-items="navActionBarItems"
    :search-label="searchLabel"
    :search-title="searchTitle"
    :cart-quick-view="cartQuickView"
    :login-quick-view="loginQuickView"
    :secondary-navigation="secondaryNavigation"
    @cart-item-remove="cartWidgetStore.handleRemove"
    @cart-type-change="(obj) => cartStore.setActiveCartType(obj.id as CartType)"
  >
    <template v-if="secondaryNavigation" #secondary-nav-cta>
      <MappedComponents :items="secondaryNavigationSlotContent ?? []" />
    </template>
  </PageHeader>
  <DistractionFreeHeader
    v-show="isCheckout"
    :logo-link="logoLink"
    :logo-text="`${$globalPageSettings?.companyName ?? ''}`"
    :user-action-items="navActionBarItems"
    :show-demo-banner="$usesDemoShop"
    :demo-banner-text="
      htmlDictionary['pageHeader.checkout.demoshop.mobile.text'] ?? 'Demo Shop'
    "
    :demo-banner-text-long="
      htmlDictionary['pageHeader.checkout.demoshop.desktop.text'] ?? 'Demo Shop'
    "
  >
    <template #secondary-bar>
      <ul
        v-if="$secondaryNavigation?.fields"
        class="distraction-free-header__breadcrumb"
      >
        <li>
          <span class="mobile-text"
            ><HyperLink>{{
              $secondaryNavigation?.fields.titleForSecondaryNavigation ??
              'Shopping on baslerweb.com'
            }}</HyperLink></span
          >
          <span class="desktop-text">
            <HyperLink
              v-bind="
                buildLinkInterface(
                  $secondaryNavigation?.fields.linkForSecondaryNavigationTitle,
                  $locale
                )
              "
              >{{
                $secondaryNavigation?.fields.titleForSecondaryNavigation ??
                'Shopping on baslerweb.com'
              }}</HyperLink
            >
          </span>
        </li>
        <li
          v-for="i in checkoutSecondaryNavigation"
          :key="i.id"
          :class="{
            'is-active': i.isActive
          }"
        >
          <span class="mobile-text"
            ><HyperLink v-bind="i.linkProps">{{ i.label }}</HyperLink></span
          >
          <span class="desktop-text"
            ><HyperLink v-bind="i.linkProps">{{ i.label }}</HyperLink></span
          >
        </li>
      </ul>
    </template>
  </DistractionFreeHeader>
  <ShopCartToastContainer v-if="!isPdp" />
  <AccountCreationModal
    v-model="showAccountCreationModal"
    :locale="$locale"
    :store="$shopStore"
    @account-created="handleAccountCreated"
    @timeout="handleAccountCreationTimeout"
    @error="handleAccountCreationError"
  />
</template>

<script setup lang="ts">
import {
  DistractionFreeHeader,
  HyperLink,
  PageHeader
} from '@hypercodestudio/basler-components';
import type { MainNavigationInterface } from '@hypercodestudio/basler-components/dist/models/navigation/MainNavigation';
import type { NavTopItem } from '@hypercodestudio/basler-components/dist/models/navTopItem';
import type { HyperlinkInterface } from '@hypercodestudio/basler-components/dist/components/helpers/HyperLink.vue';
import { useCreateMainNavItem } from '~/composables/useCreateMainNavItem';
import { useCreateSecondaryNavItem } from '~/composables/useCreateSecondaryNavItem';
import { useBuildLinkInterface } from '~/composables/useBuildLinkInterface';
import { isDefined } from '~/utils/guards/isDefined';
import { isProductDetailPage } from '~/utils/shop/isProductDetailPage';

import type {
  LinkFragment,
  NavigationItemFragment
} from '~/lib/ContentfulGraphqlService';
import { isNavigationItemFragment } from '~/utils/guards/isNavigationItemFragment';
import { deepExtract } from '~/utils/deepExtract';
import { convertContentfulToWebappLocaleCode } from '~/utils/convertContentfulToWebappLocaleCode';
import { useCartWidgetStore } from '~/stores/cartWidgetStore/cartWidgetStore';
import { useCartStore } from '~/stores/cartStore/cartStore';
import { useGeoinformationStore } from '~/stores/geoinformationStore/geoinformationStore';
import { useHTMLDictionary } from '~/composables/useHTMLDictionary';
import { buildUrlString } from '~/utils/buildUrlString';
import { buildAuthLoginUrl } from '~/utils/buildAuthLoginUrl';
import { useUserIdCookie } from '~/composables/useUserIdCookie';
import { buildLinkInterface } from '~/utils/buildLinkInterface';
import isOneOfCheckoutPages from '~/utils/shop/isOneOfCheckoutPages';
import isOneOfAccountAreaPages from '~/utils/shop/isOneOfAcountAreaPages';
import { ToastMessageType, useShowToast } from '~/composables/useShowToast';
import buildCustomerAreaUrl from '~/utils/shop/buildCustomerAreaUrl';
import {
  G4_EVENT_NAME_LOGIN,
  G4_EVENT_NAME_LOGOUT,
  G4_EVENT_NAME_SIGN_UP,
  G4_METHOD_NAME_BASLER_ID,
  QUERY_PARAM_LOGGEDIN,
  QUERY_PARAM_LOGGOUT,
  QUERY_PARAM_SIGNUP
} from '~/lib/constants';
import getBaslerIdLogoutUri from '~/utils/getBaslerIdLogoutUri';
import { createEcommerceTrackingData } from '~/stores/cartStore/helper/createEcommerceTrackingData';
import { type CartType } from '~/stores/cartStore/model';
import { useMergeCartsCookie } from '~/utils/useMergeCartsCookie';

const {
  $globalPageSettings,
  $breakpoints,
  $textDictionary,
  $locale,
  $secondaryNavigation,
  $analytics,
  $usesDemoShop,
  $shopStore
} = useNuxtApp();
const isPdp = computed(() => isProductDetailPage());

const htmlDictionary = useHTMLDictionary([
  'pageHeader.loginQuickView.introduction.text',
  'pageHeader.checkout.demoshop.mobile.text',
  'pageHeader.checkout.demoshop.desktop.text'
]);

const maintenanceMode = computed(() => {
  return $globalPageSettings.value?.shopConfig?.maintenanceMode;
});
const slug = useSlug();
const cartStore = useCartStore();
const geoinformationStore = useGeoinformationStore();
const logger = useLogger();
const route = useRoute();
const router = useRouter();
const { loggedIn, user } = useOidcAuth();
const { showToast } = useShowToast();

// Account creation modal state
const showAccountCreationModal = ref(false);

// Handle account creation success
const handleAccountCreated = async () => {
  const message =
    $textDictionary.value['auth.accountCreated.message'] ??
    'Your account has been successfully created!';

  showToast(message, {
    severity: ToastMessageType.SUCCESS
  });

  // Reload page to ensure session and stores are properly synchronized
  // This ensures the Magento session is fully initialized and the cart is loaded
  setTimeout(() => {
    if (import.meta.client) {
      window.location.reload();
    }
  }, 1500); // Give time for toast to be visible
};

// Handle account creation timeout
const handleAccountCreationTimeout = () => {
  const message =
    $textDictionary.value['auth.accountCreation.timeout.message'] ??
    'Account creation is taking longer than expected (>60 seconds). Please reload the page if problems occur.';

  showToast(message, {
    severity: ToastMessageType.WARNING
  });

  // Also reload after timeout to ensure proper state
  setTimeout(() => {
    if (import.meta.client) {
      window.location.reload();
    }
  }, 2000);
};

// Handle account creation error
const handleAccountCreationError = (statusCode: number) => {
  let message = '';

  // Customize error message based on status code
  if (statusCode >= 500) {
    message =
      $textDictionary.value['auth.accountCreation.error.server'] ??
      'A server error occurred. Please try again later.';
  } else if (statusCode === 0) {
    message =
      $textDictionary.value['auth.accountCreation.error.network'] ??
      'Network error. Please check your internet connection.';
  } else {
    message =
      $textDictionary.value['auth.accountCreation.error.generic'] ??
      `An error occurred (Code: ${statusCode}). Please contact support.`;
  }

  showToast(message, {
    severity: ToastMessageType.ERROR
  });

  // Reload page after showing error to attempt recovery
  setTimeout(() => {
    if (import.meta.client) {
      window.location.reload();
    }
  }, 3000);
};

const footerLink = computed(() =>
  useBuildLinkInterface($globalPageSettings.value?.cartFooterLink)
);
const noItemsLink = computed(() =>
  useBuildLinkInterface($globalPageSettings.value?.cartNoItemsLink)
);

const isCheckout = computed(() => {
  return isOneOfCheckoutPages($globalPageSettings, slug);
});
const checkoutSecondaryNavigation = computed(() => {
  const currentUri = buildUrlString($locale.value, slug.value);
  let previousWasActive = false;
  return (
    $secondaryNavigation.value?.fields.navigationItems.map((i) => {
      const linkInterface = buildLinkInterface(i, $locale.value);
      const active = linkInterface.uri === currentUri;

      if (!previousWasActive && active) {
        previousWasActive = true;
      }
      return {
        id: i.sys.id,
        label: i.fields.linkText,
        isActive: active,
        linkProps: previousWasActive && !active ? {} : linkInterface // No links to later navigation entries
      };
    }) ?? []
  );
});

const isAccountArea = computed(() => {
  return isOneOfAccountAreaPages($globalPageSettings, slug);
});
const cartWidgetStore = useCartWidgetStore();

watchEffect(() =>
  cartWidgetStore.$patch(() => {
    cartWidgetStore.footerLink = footerLink.value;
    cartWidgetStore.noItemsLink = noItemsLink.value;
  })
);

const mainNavigation = computed(
  () =>
    $globalPageSettings.value?.headerMainNavigation?.navigationItemsCollection
      ?.items
);

const firstLevelParentNavigationItemIndex = computed(() => {
  if (!slug.value) {
    return -1;
  }

  let parents: NavigationItemFragment[] = [];

  if ($globalPageSettings.value) {
    deepExtract(
      $globalPageSettings.value.headerMainNavigation,
      (value, _, _parents, stop) => {
        if (
          isNavigationItemFragment(value) &&
          value.internalPage?.slug === slug.value
        ) {
          parents = [..._parents.filter(isNavigationItemFragment), value];
          stop();
        }

        return false;
      }
    );
  }

  return mainNavigation.value?.findIndex(
    (item) => item?.internalPage?.slug === parents[0]?.internalPage?.slug
  );
});

const mainNavigationItems = computed<MainNavigationInterface>(() => {
  if (isCheckout.value) {
    return {} as MainNavigationInterface;
  }
  return {
    showTabs: mainNavigation.value
      ?.map((item, index) => (item?.displayedAsTabs ? index : -1))
      .filter((item) => item !== -1),
    items:
      mainNavigation.value
        ?.map((item, index) =>
          useCreateMainNavItem(
            item,
            index === firstLevelParentNavigationItemIndex.value
          )
        )
        .filter(isDefined) ?? [],
    backButtonLabel:
      $textDictionary.value['pageHeader.navigation.mobile.back'] ?? 'Back',
    nextButtonLabel:
      $textDictionary.value['pageHeader.navigation.mobile.next'] ?? 'Next',
    closeButtonLabel:
      $textDictionary.value['pageHeader.navigation.mobile.close'] ?? 'Close'
  };
});

// region Begin Workaround for QuickCartView:
// we MUST keep the reference to this array and only manipulate
// the entries inside to get the cart widget to update.
const cartsRef = ref([...cartWidgetStore.carts]);
const cartQuickView = computed(() => ({
  carts: cartsRef,
  noItems: cartWidgetStore.noItems,
  isOpen: true
}));

watch(
  () => cartWidgetStore.carts,
  () => {
    cartsRef.value.splice(0, cartsRef.value.length);

    // TODO: FIX THIS IN CARTWIDGETSTORE !!!  DBP-2905: QUICKFIX TO ACTIVATE THE LAST CART
    if (cartWidgetStore.carts.length === 1) {
      cartWidgetStore.carts[0].isActive = true;
    }

    cartsRef.value.push(...cartWidgetStore.carts);
  }
);
// End Workaround
// endregion

function mapLink(link: LinkFragment) {
  return {
    title: link.linkText,
    icon: link.linkIcon
      ? {
          name: link.linkIcon
        }
      : undefined,
    link: buildLinkInterface(link, $locale.value)
  };
}

const loginQuickView = computed(() => {
  if (import.meta.server || $locale.value === 'zh-cn') {
    return undefined;
  }

  return {
    isOpen: true,
    // logged out params
    introText:
      htmlDictionary.value['pageHeader.loginQuickView.introduction.text'],
    ctaLinks: [
      {
        title:
          $textDictionary.value[
            'pageHeader.loginQuickView.actions.login.label'
          ] ?? 'Login with Basler ID',
        styleType: 'attraction',
        link: {
          uri: buildAuthLoginUrl(route.path, route.query, 'login'),
          target: '_self',
          rel: 'nofollow'
        } satisfies HyperlinkInterface
      },
      {
        title:
          $textDictionary.value[
            'pageHeader.loginQuickView.actions.register.label'
          ] ?? 'Register personal Basler ID',
        styleType: 'link',
        link: {
          uri: buildAuthLoginUrl(route.path, route.query, 'create'),
          target: '_self',
          rel: 'nofollow'
        } satisfies HyperlinkInterface
      }
    ],
    isLoggedIn: loggedIn.value,
    // loggedin params
    profile: {
      company: user.value?.claims?.Company,
      firstName: user.value?.userInfo?.given_name,
      lastName: user.value?.userInfo?.family_name,
      email: user.value?.userInfo?.email
    },
    linkGrid: {
      items: (
        $globalPageSettings.value?.accountWidgetLinkGridCollection?.items ?? []
      )
        .filter(isDefined)
        .map(mapLink),
      numberOfColumns: 2
    },
    linkList: (
      $globalPageSettings.value?.accountWidgetLinkListCollection?.items ?? []
    )
      .filter(isDefined)
      .map(mapLink),
    footer: {
      linkItem: {
        title:
          $textDictionary.value[
            'pageHeader.loginQuickView.actions.logout.label'
          ] ?? 'Logout',
        link: {
          uri: getBaslerIdLogoutUri()
        } satisfies HyperlinkInterface
      }
    }
  };
});

const navTopItems = computed(() => {
  if (isCheckout.value) {
    return [];
  }
  return [
    ...($globalPageSettings.value?.headerServiceNavigation?.navigationItemsCollection?.items
      .filter(
        (item) => !isDefined(item?.showInNavigation) || item?.showInNavigation
      )
      .map(
        (item) =>
          ({
            label: item?.linkText ?? '',
            // XXX: wrong type in Hypercode components?
            link: useBuildLinkInterface(item) as any,
            iconName: item?.icon ?? ''
          } satisfies NavTopItem)
      ) ?? []),
    {
      label: geoinformationStore.currentRegionLabel,
      link: {
        uri: {
          // without the path the link does not work on the homepage
          path: '.',
          hash: '#regionselector'
        }
      } satisfies HyperlinkInterface,
      iconName: 'globe'
    }
  ] satisfies NavTopItem[];
});

// the cart links needs the absolute url to be treated as default link
// (not <RouterLink>).
const cartLink = computed(() => {
  if (
    !cartWidgetStore.currentCartLink ||
    // desktop should not have a basket link
    !$breakpoints.specialHeaderBreakpoint
  ) {
    return {};
  }

  return {
    uri: cartWidgetStore.currentCartLink,
    target: '_self',
    external: true
  };
});

const navActionBarItems = computed(() => {
  let userInitials;

  if (loggedIn.value && user.value?.userInfo) {
    userInitials =
      ((user.value?.userInfo?.given_name as string)[0] ?? '').toUpperCase() +
      ((user.value?.userInfo?.family_name as string)[0] ?? '').toUpperCase();
  }

  if (isCheckout.value) {
    return [
      {
        name: 'profile',
        type: 'login',
        title:
          $textDictionary.value['pageHeader.actions.shopLogin'] ?? 'Shop login',
        link: profileLink.value,
        iconName: loggedIn.value ? undefined : 'action_bar_profile',
        userInitials
      }
    ];
  }

  return [
    {
      name: 'search',
      type: 'search',
      title: 'Search',
      // link: useBuildLinkInterface('/search/'),
      link: useBuildLinkInterface($globalPageSettings.value?.searchResultsPage),
      iconName: 'action_bar_magnifier'
    },
    {
      name: 'cart',
      type: 'cart',
      title: $textDictionary.value['cart.cart.title.label'] ?? 'Cart',
      link: cartLink.value,
      iconName: 'action_bar_shopping_cart',
      badgeNumber: cartWidgetStore.cartItemCount
    },
    {
      name: 'profile',
      type: 'login',
      title:
        $textDictionary.value['pageHeader.actions.shopLogin'] ?? 'Shop login',
      link: profileLink.value,
      iconName: loggedIn.value ? undefined : 'action_bar_profile',
      userInitials
    }
  ].filter((item) => item.name !== 'cart' || maintenanceMode.value !== true);
});

const searchLabel = computed(() => 'Search...');
const searchTitle = computed(() => 'Search');
const logoLink = computed(() => ({
  uri: `/${convertContentfulToWebappLocaleCode($locale.value)}/`
}));
const profileLink = computed(() => {
  return {
    uri:
      buildCustomerAreaUrl('customerAccountOverviewPage') ||
      cartStore.profileLink,
    // Only open in new tab when in checkout, otherwise same tab
    target: isCheckout.value ? '_blank' : '_self',
    external: true
  };
});

const mergeCartsCookie = useMergeCartsCookie();
watch(
  mergeCartsCookie,
  async () => {
    if (
      import.meta.client &&
      isDefined(mergeCartsCookie.value) &&
      typeof mergeCartsCookie.value === 'object' &&
      typeof mergeCartsCookie.value[$shopStore.value] === 'object' &&
      'necessary' in mergeCartsCookie.value[$shopStore.value] &&
      mergeCartsCookie.value[$shopStore.value].necessary
    ) {
      // Delegate the merge logic to the cart store
      // The toast will be shown automatically via the toast cookie set by the server
      await cartStore.checkAndMergeGuestCart();
    }
    mergeCartsCookie.value = null;
  },
  { immediate: true }
);

onMounted(() => {
  // this loads all carts for which a cookie value exists.
  // we trigger this on the client side as this is user data and should not
  // be cached (e.g. in a cdn).
  cartStore.init(true).catch((e) => {
    // the cart store has internal error handling. just in case wrap with an
    // additional try-catch.
    logger.error('PageHeader', 'could not load cart', e);
  });

  if (QUERY_PARAM_LOGGEDIN in route.query) {
    const userId = user?.value?.claims?.oid?.toString() ?? 'null';
    $analytics.pushToDataLayer({
      event: G4_EVENT_NAME_LOGIN,
      method: G4_METHOD_NAME_BASLER_ID,
      userId: userId,
      ecommerce: createEcommerceTrackingData({
        cartItems: cartStore.activeCartItems
          .map((item) => {
            return {
              quantity: item.quantity,
              decoratedProduct: item.decoratedProduct
            };
          })
          .filter(isDefined)
      })
    });
    useUserIdCookie().value = userId;

    const currentQuery = { ...route.query };

    delete currentQuery[QUERY_PARAM_LOGGEDIN];
    router.push({ query: currentQuery });
  }

  if (QUERY_PARAM_LOGGOUT in route.query) {
    $analytics.pushToDataLayer({
      event: G4_EVENT_NAME_LOGOUT,
      method: G4_METHOD_NAME_BASLER_ID,
      userId: 'null',
      ecommerce: createEcommerceTrackingData({
        cartItems: cartStore.activeCartItems
          .map((item) => {
            return {
              quantity: item.quantity,
              decoratedProduct: item.decoratedProduct
            };
          })
          .filter(isDefined)
      })
    });
    useUserIdCookie().value = 'null';

    const message =
      $textDictionary.value['auth.loggedout.message'] ??
      'Successfully logged out';

    showToast(message, {
      severity: ToastMessageType.SUCCESS
    });

    const currentQuery = { ...route.query };

    delete currentQuery[QUERY_PARAM_LOGGOUT];
    router.push({ query: currentQuery });
  }

  if (QUERY_PARAM_SIGNUP in route.query) {
    const userId = user?.value?.claims?.oid?.toString() ?? 'null';
    $analytics.pushToDataLayer({
      event: G4_EVENT_NAME_SIGN_UP,
      method: G4_METHOD_NAME_BASLER_ID,
      userId: userId,
      ecommerce: createEcommerceTrackingData({
        cartItems: cartStore.activeCartItems
          .map((item) => {
            return {
              quantity: item.quantity,
              decoratedProduct: item.decoratedProduct
            };
          })
          .filter(isDefined)
      })
    });
    useUserIdCookie().value = userId;

    // Show account creation modal
    // The modal will handle polling and emit events when done
    showAccountCreationModal.value = true;

    const currentQuery = { ...route.query };

    delete currentQuery[QUERY_PARAM_SIGNUP];
    router.push({ query: currentQuery });
  }
});

const secondaryNavigationSlotContent = computed(
  () => $secondaryNavigation.value?.fields?.linksForSecondaryNavigation
);

const secondaryNavigation = computed(() => {
  if (isAccountArea.value) {
    return null;
  }

  const fields = $secondaryNavigation.value?.fields;
  if (!fields) {
    return null;
  }

  return {
    title: fields?.iconForSecondaryNavigation
      ? {
          level: 3,
          tag: 'div',
          content: fields?.titleForSecondaryNavigation ?? '',
          icon: {
            name: fields?.iconForSecondaryNavigation ?? '',
            scaleFactor: 1.15
          }
        }
      : {
          level: 3,
          tag: 'div',
          content: fields?.titleForSecondaryNavigation ?? ''
        },
    titleLink: fields?.linkForSecondaryNavigationTitle
      ? buildLinkInterface(
          fields?.linkForSecondaryNavigationTitle,
          $locale.value
        )
      : {
          uri: '#top',
          external: false,
          target: '_self'
        },
    items:
      fields?.navigationItems
        ?.map((item) => useCreateSecondaryNavItem(item))
        .filter(isDefined) ?? []
  };
});
</script>
